/**
 * Melody Interval Demo
 *
 * Demonstrates that melodies are defined by RELATIVE intervals (scale degrees),
 * not absolute note names. The same melody played in different keys still sounds
 * like the same song!
 *
 * Uses "Ode to Joy" - a simple, universally recognizable melody.
 */

import React, { useState, useCallback } from "react";
import { useSoundLibrary } from "../../soundLibrary";
import { Note } from "../../types/sound";
import styles from "../../styles/MelodyIntervalDemo.module.css";

// "Ode to Joy" melody (first half of main theme)
// Scale degrees: 3-3-4-5-5-4-3-2 | 1-1-2-3-3-2-2
const ODE_TO_JOY = [
    { degree: 3, duration: 0.5 },  // Mi
    { degree: 3, duration: 0.5 },  // Mi
    { degree: 4, duration: 0.5 },  // Fa
    { degree: 5, duration: 0.5 },  // Sol
    { degree: 5, duration: 0.5 },  // Sol
    { degree: 4, duration: 0.5 },  // Fa
    { degree: 3, duration: 0.5 },  // Mi
    { degree: 2, duration: 0.5 },  // Re
    { degree: 1, duration: 0.5 },  // Do
    { degree: 1, duration: 0.5 },  // Do
    { degree: 2, duration: 0.5 },  // Re
    { degree: 3, duration: 0.5 },  // Mi
    { degree: 3, duration: 0.75 }, // Mi (dotted quarter)
    { degree: 2, duration: 0.25 }, // Re (eighth note)
    { degree: 2, duration: 1.0 },  // Re (half note - final)
];

// Major scale starting from different root notes
const MAJOR_SCALE_PATTERNS: Record<string, Note[]> = {
    "C Major": ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5"],
    "F Major": ["F3", "G3", "A3", "A#3", "C4", "D4", "E4", "F4"],
    "G Major": ["G3", "A3", "B3", "C4", "D4", "E4", "F#4", "G4"],
};

const INSTRUMENTS = ["piano", "cello", "viola"] as const;

export default function MelodyIntervalDemo() {
    const { loadInstrument, playNote } = useSoundLibrary();
    const [selectedKey, setSelectedKey] = useState<keyof typeof MAJOR_SCALE_PATTERNS>("C Major");
    const [selectedInstrument, setSelectedInstrument] = useState<typeof INSTRUMENTS[number]>("piano");
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeNoteIndex, setActiveNoteIndex] = useState<number | null>(null);

    const handlePlay = useCallback(async () => {
        if (isPlaying) return;

        setIsPlaying(true);
        await loadInstrument(selectedInstrument);

        // Build the melody from scale degrees and play with proper timing
        const scale = MAJOR_SCALE_PATTERNS[selectedKey];

        for (let i = 0; i < ODE_TO_JOY.length; i++) {
            const note = ODE_TO_JOY[i];
            setActiveNoteIndex(i);
            const noteName = scale[note.degree - 1];
            // Lower volume for viola
            const velocity = selectedInstrument === "viola" ? 0.5 : 0.8;
            playNote(selectedInstrument, noteName, {
                duration: note.duration,
                velocity,
            });
            await new Promise(resolve => setTimeout(resolve, note.duration * 1000));
        }

        setActiveNoteIndex(null);
        setIsPlaying(false);
    }, [isPlaying, loadInstrument, selectedInstrument, selectedKey, playNote]);

    return (
        <div className={styles.demoContainer}>
            <div className={styles.header}>
                <h4 className={styles.title}>Melody Detective: Ode to Joy</h4>
                <p className={styles.subtitle}>
                    Hear how the same melody sounds in different keys and instruments
                </p>
            </div>

            <div className={styles.degreesDisplay}>
                <p className={styles.degreesLabel}>
                    <strong>Scale Degrees:</strong> This melody is defined by RELATIVE patterns,
                    not absolute notes
                </p>
                <div className={styles.degreesRow}>
                    {ODE_TO_JOY.map((note, index) => (
                        <span
                            key={index}
                            className={`${styles.degreeBadge} ${
                                note.duration > 0.5 ? styles.longNote : ""
                            } ${
                                activeNoteIndex === index ? styles.active : ""
                            }`}
                        >
                            {note.degree}
                        </span>
                    ))}
                </div>
                <p className={styles.rhythmHint}>
                    Longer notes at the end show how rhythm affects melody
                </p>
            </div>

            <div className={styles.controls}>
                <div className={styles.controlGroup}>
                    <label className={styles.label}>Key:</label>
                    <div className={styles.buttonGroup}>
                        {(Object.keys(MAJOR_SCALE_PATTERNS) as Array<keyof typeof MAJOR_SCALE_PATTERNS>).map(
                            (key) => (
                                <button
                                    key={key}
                                    className={`${styles.keyButton} ${
                                        selectedKey === key ? styles.active : ""
                                    }`}
                                    onClick={() => setSelectedKey(key)}
                                    disabled={isPlaying}
                                >
                                    {key}
                                </button>
                            )
                        )}
                    </div>
                </div>

                <div className={styles.controlGroup}>
                    <label className={styles.label}>Instrument:</label>
                    <div className={styles.buttonGroup}>
                        {INSTRUMENTS.map((instrument) => (
                            <button
                                key={instrument}
                                className={`${styles.instrumentButton} ${
                                    selectedInstrument === instrument ? styles.active : ""
                                }`}
                                onClick={() => setSelectedInstrument(instrument)}
                                disabled={isPlaying}
                            >
                                {instrument === "cello"
                                    ? "Guitar"
                                    : instrument.charAt(0).toUpperCase() + instrument.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <button
                className={styles.playButton}
                onClick={handlePlay}
                disabled={isPlaying}
            >
                {isPlaying ? "♪ Playing..." : "▶ Play Ode to Joy"}
            </button>

            <div className={styles.noteNames}>
                <p className={styles.noteNamesLabel}>
                    Notes in {selectedKey}:
                </p>
                <p className={styles.noteNamesSequence}>
                    {ODE_TO_JOY.map((note) => {
                        const noteValue = MAJOR_SCALE_PATTERNS[selectedKey][note.degree - 1];
                        // Convert A# to Bb for F Major (more common notation)
                        const displayNote: string = noteValue === "A#3" ? "Bb3" : noteValue;
                        return (
                            <span
                                key={`${note.degree}-${note.duration}`}
                                className={`${styles.noteName} ${note.duration > 0.5 ? styles.longNote : ""}`}
                            >
                                {displayNote}
                            </span>
                        );
                    })}
                </p>
            </div>

            <div className={styles.insight}>
                <p className={styles.insightText}>
                    <strong>🎵 Key insight:</strong> Notice how changing the key changes the NOTE
                    NAMES, but the melody sounds the same! That's because your ear recognizes the
                    INTERVALS (the distance between notes), not the absolute pitches. This is why
                    ear training focuses on intervals and scale degrees.
                </p>
            </div>
        </div>
    );
}
